import { useEffect, type KeyboardEvent } from "react";
import { Bot, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { store, useStore } from "@/lib/store";
import type { PlatformAssistant } from "@/lib/platformTypes";
import { ConnectPlatformCard, EmptyState, ViewSection } from "@/views/shared";

export function statusVariant(status: string | null): "default" | "secondary" | "outline" {
  if (status === "active") return "default";
  if (status === "archived") return "secondary";
  return "outline";
}

function AssistantRow({ assistant }: { assistant: PlatformAssistant }) {
  const open = () => store.navigate({ name: "assistant", id: assistant.id });
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  return (
    // A div-with-role instead of <button> so the chat URL can nest as a real
    // <a> without producing invalid interactive-inside-interactive markup.
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
      className="glass-border w-full cursor-pointer rounded-xl border p-4 text-left transition-colors hover:border-primary/50"
    >
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 shrink-0 text-primary" />
        <span className="min-w-0 truncate text-sm font-semibold">{assistant.name}</span>
        <Badge variant={statusVariant(assistant.status)} className="ml-auto shrink-0">
          {assistant.status ?? "unknown"}
        </Badge>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">{assistant.slug}</span>
        {assistant.model && (
          <Badge variant="outline" className="font-mono text-[10px] font-normal">
            {assistant.model}
          </Badge>
        )}
        {assistant.chatUrl && (
          <a
            href={assistant.chatUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
          >
            chat
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

export function AssistantsView() {
  const { platform } = useStore();
  const { available, assistants } = platform;

  // Deep links can land here before anything has fetched the platform
  // overview; loaded flips true even on failure, so this cannot loop.
  useEffect(() => {
    if (available && !assistants.loaded) {
      void store.loadPlatformOverview();
    }
  }, [available, assistants.loaded]);

  if (!available) return <ConnectPlatformCard />;

  return (
    <div className="mx-auto w-full max-w-3xl flex-1 overflow-y-auto p-4">
      <ViewSection title="Assistants">
        {!assistants.loaded ? (
          <div className="space-y-3">
            <Skeleton className="h-[76px] w-full rounded-xl" />
            <Skeleton className="h-[76px] w-full rounded-xl" />
            <Skeleton className="h-[76px] w-full rounded-xl" />
          </div>
        ) : assistants.error ? (
          <div className="glass-border rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {assistants.error}
          </div>
        ) : assistants.items.length === 0 ? (
          <EmptyState message="No assistants yet. Deploy one from Claude with the deploy_assistant tool on opzero.sh." />
        ) : (
          <div className="space-y-3">
            {assistants.items.map((a) => (
              <AssistantRow key={a.id} assistant={a} />
            ))}
          </div>
        )}
      </ViewSection>
    </div>
  );
}
